/**
 * WP14 (docs/decisions/2026-09-24-wp14-kierunek-transportu-i-odbior-wlasny.md):
 * every transport has a direction, and it alone decides who pays its freight:
 *   INBOUND  — od dostawcy: freight goes into the ZD batches only;
 *   OUTBOUND — do klienta: freight is split across the customer orders only.
 * The money rule lives in the backend (transportService); this module is the
 * UI mirror of backend/src/utils/transportDirection.js — values, the default
 * for a new truck and the Polish label keys.
 */
export type TransportDirection = 'INBOUND' | 'OUTBOUND';
export type OrderDeliveryMode = 'OUR_DELIVERY' | 'CUSTOMER_PICKUP';

export const TRANSPORT_DIRECTIONS: TransportDirection[] = ['INBOUND', 'OUTBOUND'];
export const ORDER_DELIVERY_MODES: OrderDeliveryMode[] = ['OUR_DELIVERY', 'CUSTOMER_PICKUP'];

export function normalizeTransportDirection(value: unknown): TransportDirection | null {
  return value === 'INBOUND' || value === 'OUTBOUND' ? value : null;
}

export function normalizeOrderDeliveryMode(value: unknown): OrderDeliveryMode {
  return value === 'CUSTOMER_PICKUP' ? 'CUSTOMER_PICKUP' : 'OUR_DELIVERY';
}

/**
 * Suggested direction of a new truck until the operator picks one: a truck
 * carrying a supplier order comes from the supplier (also with customer orders
 * on board — the direct delivery), otherwise it goes to the customer. Same rule
 * as the backend default for a writer that omits the direction.
 */
export function deriveTransportDirection(
  supplierOrderCount: number,
): TransportDirection {
  return supplierOrderCount > 0 ? 'INBOUND' : 'OUTBOUND';
}

export function isCustomerPickup(order?: { deliveryMode?: OrderDeliveryMode | null } | null): boolean {
  return order?.deliveryMode === 'CUSTOMER_PICKUP';
}

/**
 * Rodzaj auta (decision 2026-10-04, point 7) — mirror of backend
 * utils/transportDirection.deriveTransportKind. Derived from the stored data
 * (direction + customer orders on board), never saved:
 *   FACTORY_TO_CUSTOMER   „Fabryka → klient”  (INBOUND, ZD + ZK);
 *   FACTORY_TO_WAREHOUSE  „Fabryka → magazyn” (INBOUND, only ZD);
 *   WAREHOUSE_TO_CUSTOMER „Magazyn → klient”  (OUTBOUND, only ZK).
 */
export type TransportKind = 'FACTORY_TO_CUSTOMER' | 'FACTORY_TO_WAREHOUSE' | 'WAREHOUSE_TO_CUSTOMER';
export const TRANSPORT_KINDS: TransportKind[] = ['FACTORY_TO_CUSTOMER', 'FACTORY_TO_WAREHOUSE', 'WAREHOUSE_TO_CUSTOMER'];

export function normalizeTransportKind(value: unknown): TransportKind | null {
  return TRANSPORT_KINDS.includes(value as TransportKind) ? value as TransportKind : null;
}

export function deriveTransportKind(truck: {
  direction?: TransportDirection | null;
  customerOrderCount?: number;
  supplierOrderCount?: number;
}): TransportKind {
  const direction = truck.direction || deriveTransportDirection(truck.supplierOrderCount || 0);
  if (direction === 'OUTBOUND') return 'WAREHOUSE_TO_CUSTOMER';
  return (truck.customerOrderCount || 0) > 0 ? 'FACTORY_TO_CUSTOMER' : 'FACTORY_TO_WAREHOUSE';
}

/** Who pays a truck of this kind (the backend money rule follows it). */
export function transportKindDirection(kind: TransportKind): TransportDirection {
  return kind === 'WAREHOUSE_TO_CUSTOMER' ? 'OUTBOUND' : 'INBOUND';
}

/** The kind carries customer orders (ZK). */
export const kindCarriesCustomerOrders = (kind: TransportKind | null | undefined): boolean => (
  kind === 'FACTORY_TO_CUSTOMER' || kind === 'WAREHOUSE_TO_CUSTOMER'
);
/** The kind carries supplier orders (ZD). */
export const kindCarriesSupplierOrders = (kind: TransportKind | null | undefined): boolean => (
  kind === 'FACTORY_TO_CUSTOMER' || kind === 'FACTORY_TO_WAREHOUSE'
);

export const transportKindLabelKey = (kind: TransportKind) => `transport:kind.options.${kind}.label`;

/** i18n keys in the `transport` namespace. */
export const transportDirectionLabelKey = (direction: TransportDirection) => `transport:direction.options.${direction}`;
export const transportDirectionShortKey = (direction: TransportDirection) => `transport:direction.short.${direction}`;

/** i18n keys in the `orders` namespace. */
export const orderDeliveryModeLabelKey = (mode: OrderDeliveryMode) => `orders:deliveryMode.options.${mode}`;
